<template>
  <div class="device-ssd-health-comparison">
    <div v-if="loading" class="d-flex justify-center align-center py-12">
      <Loader />
    </div>

    <template v-else-if="drives.length">
      <v-data-table :headers="summaryHeaders" :items="summaryItems" :items-per-page="10" dense class="mb-4">
        <template v-slot:item.series="{ item }">
          <svg width="28" height="12" class="d-block">
            <line
              x1="0"
              y1="6"
              x2="28"
              y2="6"
              :stroke="item.color"
              stroke-width="2"
              :stroke-dasharray="item.dashArray || undefined"
            />
            <circle cx="14" cy="6" r="4" :fill="item.color" />
          </svg>
        </template>
        <template v-slot:item.device_id="{ item }">
          <router-link
            :to="{ name: 'next-stop-device-config', params: { id: item.device_id } }"
            class="cursor-pointer text-decoration-none"
          >
            <span class="font-weight-semibold text--primary">{{ item.device_id }}</span>
          </router-link>
        </template>
        <template v-slot:item.graphed_metric="{ item }">
          {{ formatValue(item.graphed_metric) }}
        </template>
      </v-data-table>

      <div class="d-flex align-center flex-wrap mb-2">
        <span class="text-caption me-2">Field:</span>
        <v-chip
          v-for="field in availableFields"
          :key="field.key"
          small
          class="mr-2 mb-1"
          color="primary"
          :outlined="field.key !== activeFieldKey"
          :text-color="field.key === activeFieldKey ? 'white' : 'primary'"
          @click="selectedField = field.key"
        >
          {{ field.label }}
        </v-chip>
      </div>

      <vue-apex-charts type="line" height="400" :options="chartOptions" :series="chartSeries"></vue-apex-charts>
    </template>

    <div v-else class="text-center py-8 text--secondary">{{ emptyMessage }}</div>
  </div>
</template>

<script>
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import VueApexCharts from "vue-apexcharts"
import Loader from "@/components/Loader.vue"
import { getSeriesColor } from "@/utils/chartColors"
import {
  SSD_AXIS_LABELS,
  availableSsdFields,
  formatSsdValue,
  groupSsdPointsByDrive,
  toSsdPoints,
  toSsdSeriesData,
} from "@/utils/ssdHealth"

dayjs.extend(utc)
dayjs.extend(timezone)

const DEFAULT_FIELD = "remaining_life_pct"

export default {
  name: "DeviceSsdHealthComparison",

  components: {
    VueApexCharts,
    Loader,
  },

  props: {
    deviceIds: {
      type: Array,
      default: () => [],
    },
    recordsByDevice: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    rangeStart: {
      type: Number,
      default: null,
    },
    rangeEnd: {
      type: Number,
      default: null,
    },
    emptyMessage: {
      type: String,
      default: "No SSD health data for the selected range.",
    },
  },

  data() {
    return {
      selectedField: DEFAULT_FIELD,
    }
  },

  computed: {
    // One entry per drive per device, so a device reporting two disks gets a
    // line each rather than a single line zig-zagging between them
    drives() {
      return this.deviceIds.flatMap((deviceId, deviceIndex) => {
        const points = toSsdPoints(this.recordsByDevice[deviceId])
        const deviceDrives = groupSsdPointsByDrive(points)

        return deviceDrives.map((drive, driveIndex) => ({
          ...drive,
          deviceId,
          deviceIndex,
          driveIndex,
          multipleDrives: deviceDrives.length > 1,
        }))
      })
    },

    allPoints() {
      return this.drives.flatMap(drive => drive.points)
    },

    availableFields() {
      return availableSsdFields(this.allPoints)
    },

    activeField() {
      return (
        this.availableFields.find(field => field.key === this.selectedField) ||
        this.availableFields.find(field => field.key === DEFAULT_FIELD) ||
        this.availableFields[0] ||
        null
      )
    },

    activeFieldKey() {
      return this.activeField?.key
    },

    chartSeries() {
      if (!this.activeField) {
        return []
      }

      return this.drives.map(drive => ({
        name: this.seriesName(drive),
        data: toSsdSeriesData(drive.points, this.activeField.key),
      }))
    },

    chartOptions() {
      const formatNZ = (value, format) => dayjs(value).tz("Pacific/Auckland").format(format)

      return {
        chart: {
          type: "line",
          toolbar: { show: true },
          zoom: { enabled: true },
        },
        colors: this.drives.map(drive => this.seriesColor(drive)),
        stroke: {
          curve: "straight",
          width: 1,
          dashArray: this.drives.map(drive => this.seriesDash(drive)),
        },
        markers: { size: 2, strokeWidth: 0 },
        xaxis: {
          type: "datetime",
          min: this.rangeStart ?? undefined,
          max: this.rangeEnd ?? undefined,
          labels: {
            datetimeUTC: false,
            formatter: value => formatNZ(value, "DD MMM HH:mm"),
          },
        },
        yaxis: {
          title: { text: this.activeField ? SSD_AXIS_LABELS[this.activeField.axis] : undefined },
          labels: { formatter: formatSsdValue },
        },
        legend: { show: true, position: "top" },
        tooltip: {
          x: {
            formatter: value => formatNZ(value, "YYYY-MM-DD HH:mm"),
          },
          y: {
            formatter: formatSsdValue,
          },
        },
      }
    },

    summaryHeaders() {
      return [
        { text: "", value: "series", sortable: false, width: "40px" },
        { text: "Device", value: "device_id" },
        { text: "Model", value: "model" },
        { text: "Serial", value: "serial" },
        { text: "Firmware", value: "firmware" },
        { text: "Capacity", value: "capacity" },
        { text: "Last Reported", value: "last_reported" },
        {
          text: `Graphed Metric: ${this.activeField ? this.activeField.label : "-"}`,
          value: "graphed_metric",
          align: "end",
        },
      ]
    },

    summaryItems() {
      return this.drives.map(drive => ({
        color: this.seriesColor(drive),
        dashArray: this.seriesDash(drive),
        device_id: drive.deviceId,
        model: drive.summary.model,
        serial: drive.summary.serial,
        firmware: drive.summary.firmware,
        capacity: drive.summary.capacity,
        last_reported: drive.summary.lastReported,
        graphed_metric: this.latestValue(drive, this.activeField),
      }))
    },
  },

  methods: {
    formatValue: formatSsdValue,
    // The chart's marks and the summary table's swatches both read from these,
    // so the table's colour key always matches what is drawn. Devices also keep
    // the colour they carry on the diagnostics chart above.
    seriesColor(drive) {
      return getSeriesColor(drive.deviceIndex)
    },
    // A device reporting more than one drive separates them by dash pattern,
    // since both lines share the device's colour
    seriesDash(drive) {
      return drive.driveIndex * 4
    },
    // The drive's most recent reading of the charted field, which is not always
    // its last snapshot when that snapshot left the attribute out
    latestValue(drive, field) {
      if (!field) {
        return null
      }

      const reported = drive.points.filter(point => Number.isFinite(point[field.key]))

      return reported.length ? reported[reported.length - 1][field.key] : null
    },
    seriesName(drive) {
      return drive.multipleDrives ? `${drive.deviceId} · ${drive.id}` : drive.deviceId
    },
  },
}
</script>
